import type { ReactNode } from "react";
import { ChevronLeft, XIcon } from "lucide-react";

import { Button } from "~/components/ui/button";
import { Sheet, SheetContent, SheetTitle } from "~/components/ui/sheet";
import { useIsMobile } from "~/hooks/use-mobile";
import { cn } from "~/lib/utils";

interface MasterDetailProps {
  children: ReactNode;
  detail: ReactNode | null;
  open: boolean;
  onClose: () => void;
  emptyHint?: ReactNode;
  title?: string;
}

export function MasterDetail({
  children,
  detail,
  open,
  onClose,
  emptyHint,
  title = "Details",
}: MasterDetailProps) {
  const isMobile = useIsMobile();
  const showing = open && !!detail;

  if (isMobile) {
    return (
      <>
        <div className="@container">{children}</div>
        <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
          <SheetContent
            side="bottom"
            showCloseButton={false}
            className="flex h-svh flex-col border-0"
            style={{ paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" }}
          >
            <div className="border-hairline flex h-12 shrink-0 items-center border-b px-1">
              <Button variant="ghost" className="h-11 px-3 text-[15px]" onClick={onClose}>
                <ChevronLeft className="size-5" /> Back
              </Button>
              <SheetTitle className="sr-only">{title}</SheetTitle>
            </div>
            <div className="flex min-h-0 flex-1 flex-col">{detail}</div>
          </SheetContent>
        </Sheet>
      </>
    );
  }

  return (
    <div className="flex items-start gap-4">
      <div className="@container min-w-0 flex-1">{children}</div>
      <aside
        className={cn(
          "border-hairline bg-surface sticky top-4 max-h-[calc(100vh-6rem)] w-[360px] shrink-0 flex-col overflow-hidden rounded-lg border xl:w-[440px]",
          showing ? "flex" : "hidden 2xl:flex",
        )}
      >
        {showing ? (
          <>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close details"
              className="text-text-faint hover:text-foreground hover:bg-accent focus-visible:ring-ring/40 absolute top-3 right-3 z-10 inline-flex size-7 items-center justify-center rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2"
            >
              <XIcon className="size-3.5" />
            </button>
            {detail}
          </>
        ) : (
          <div className="text-text-faint flex flex-1 items-center justify-center p-10 text-center text-[13px]">
            {emptyHint ?? "Select a row to see its details"}
          </div>
        )}
      </aside>
    </div>
  );
}
